import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

const GUEST_CART_KEY = "3t_guest_cart";

const getGuestCart = () => {
  try {
    const stored = localStorage.getItem(GUEST_CART_KEY);
    return stored ? JSON.parse(stored) : { items: [] };
  } catch { return { items: [] }; }
};

const saveGuestCart = (cart) => {
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
};

export const CartProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    } else {
      setCart(getGuestCart());
    }
  }, [isAuthenticated, token]);

  const fetchCart = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCart(response.data);
    } catch (error) {
      console.error("Cart fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductData = async (productId) => {
    try {
      const res = await axios.get(`${API}/products/${productId}`);
      return res.data;
    } catch { return null; }
  };

  const addToCart = async (productId, quantity, size, color) => {
    if (isAuthenticated && token) {
      try {
        await axios.post(
          `${API}/cart/add`,
          { product_id: productId, quantity, size, color },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await fetchCart();
        return true;
      } catch (error) {
        console.error("Add to cart error:", error);
        return false;
      }
    } else {
      const product = await fetchProductData(productId);
      if (!product) return false;
      const guestCart = getGuestCart();
      const existingIndex = guestCart.items.findIndex(
        (i) => i.product_id === productId && i.size === size && i.color === color
      );
      if (existingIndex >= 0) {
        guestCart.items[existingIndex].quantity += quantity;
      } else {
        guestCart.items.push({ product_id: productId, quantity, size, color, product });
      }
      saveGuestCart(guestCart);
      setCart({ ...guestCart });
      return true;
    }
  };

  const updateCart = async (productId, quantity, size, color) => {
    if (isAuthenticated && token) {
      try {
        await axios.put(
          `${API}/cart/update`,
          { product_id: productId, quantity, size, color },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await fetchCart();
        return true;
      } catch (error) {
        console.error("Update cart error:", error);
        return false;
      }
    } else {
      const guestCart = getGuestCart();
      const idx = guestCart.items.findIndex(
        (i) => i.product_id === productId && i.size === size && i.color === color
      );
      if (idx >= 0) {
        if (quantity <= 0) {
          guestCart.items.splice(idx, 1);
        } else {
          guestCart.items[idx].quantity = quantity;
        }
      }
      saveGuestCart(guestCart);
      setCart({ ...guestCart });
      return true;
    }
  };

  const removeFromCart = async (productId, size, color) => {
    if (isAuthenticated && token) {
      try {
        await axios.delete(
          `${API}/cart/remove/${productId}?size=${encodeURIComponent(size)}&color=${encodeURIComponent(color)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await fetchCart();
        return true;
      } catch (error) {
        console.error("Remove from cart error:", error);
        return false;
      }
    } else {
      const guestCart = getGuestCart();
      guestCart.items = guestCart.items.filter(
        (i) => !(i.product_id === productId && i.size === size && i.color === color)
      );
      saveGuestCart(guestCart);
      setCart({ ...guestCart });
      return true;
    }
  };

  const clearCart = async () => {
    if (isAuthenticated && token) {
      try {
        await axios.delete(`${API}/cart/clear`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCart({ items: [] });
        return true;
      } catch (error) {
        console.error("Clear cart error:", error);
        return false;
      }
    } else {
      saveGuestCart({ items: [] });
      setCart({ items: [] });
      return true;
    }
  };

  const getCartTotal = () => {
    return cart.items.reduce((total, item) => {
      return total + (item.product?.price || 0) * item.quantity;
    }, 0);
  };

  const getCartCount = () => {
    return cart.items.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addToCart,
        updateCart,
        removeFromCart,
        clearCart,
        fetchCart,
        getCartTotal,
        getCartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
